import { saveAs } from 'file-saver';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { getFileTypeFromName } from 'domain/shared/artifact/utils';
import { ClientPortalTaskAttachment } from '../types';
import { BlobPreviewModal, useBlobPreview } from 'framework/ui/Blob';
import React, { useState } from 'react';
import { useUiTheme } from 'framework/uiTheme';
import { RESIZE_SIZES, useArtifactPreviewHelper } from '../../../../shared/artifact';

type Attachment = ClientPortalTaskAttachment & { file?: File; canRemove?: boolean };

const ClientPortalAttachmentsList = ({ attachments }: { attachments: Attachment[] }) => {
  const { AttachmentsListLayout } = useUiTheme();

  const { getDocumentToken } = useDocumentStorageContext();
  const { addMessage } = useMessageService();
  const { blobUrl, loading, setLoading, previewOpen, setPreviewOpen, closePreview, cleanup, setBlobUrl } =
    useBlobPreview();
  const [previewFileName, setPreviewFileName] = useState('');
  const { isPreviewAvailable } = useArtifactPreviewHelper();

  const attachmentsInfo = attachments.map(attachment => {
    const documentType = getFileTypeFromName(attachment.fileName);
    return {
      id: attachment.id,
      title: attachment.fileName,
      type: documentType,
      hasPreview: isPreviewAvailable(documentType),
    };
  });

  const handleClosePreview = () => {
    closePreview();
    cleanup();
  };

  const download = async (id: number | string, fileName?: string) => {
    try {
      const url = await getDocumentToken(id.toString());
      const response = await fetch(url);
      if (!response.ok) addMessage(new ErrorMessage('Failed to download attachment'));
      saveAs(await response.blob(), fileName);
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const openPreview = async (id: number | string) => {
    setLoading(true);
    try {
      const url = await getDocumentToken(id.toString());
      const name = attachmentsInfo.find(v => v.id === id)?.title;
      setBlobUrl(url);
      setPreviewFileName(name || 'preview');
      setPreviewOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AttachmentsListLayout
        attachments={attachmentsInfo}
        downloadFile={download}
        getThumbnail={(id: number | string, size: string) => {
          if (size === RESIZE_SIZES.SIZE_1024) {
            return getDocumentToken(id.toString());
          } else return Promise.resolve(null);
        }}
        openPreview={openPreview}
        isDownloadAvailable={true}
      />

      <BlobPreviewModal
        open={previewOpen}
        blobUrl={blobUrl}
        fileName={previewFileName}
        onClose={handleClosePreview}
        loading={loading}
      />
    </>
  );
};

export default ClientPortalAttachmentsList;
